import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Subscription, finalize } from 'rxjs';

import { FunctionSource } from '../../core/api/models/function.model';
import { SyncServiceSummary } from '../../core/api/models/sync-service.model';
import {
  OutboundIpResult,
  ServiceHealthReport,
  ServiceHealthSummary,
  SystemHealthReport,
  SystemHealthSummary,
  SystemHealthTarget,
} from '../../core/api/models/system-health.model';
import { SyncServicesApiService } from '../../core/api/services/sync-services-api.service';
import { SystemHealthApiService } from '../../core/api/services/system-health-api.service';

interface ServiceCard {
  target: SystemHealthTarget;
  service: string;
  label: string;
  description: string;
  icon: string;
  source?: FunctionSource;
}

@Component({
  selector: 'app-sync-services',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, DialogModule],
  templateUrl: './sync-services.component.html',
  styleUrl: './sync-services.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncServicesComponent implements OnInit {
  private readonly syncServicesApi = inject(SyncServicesApiService);
  private readonly systemHealthApi = inject(SystemHealthApiService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly cards: ServiceCard[] = [
    { target: 'Scheduler', service: 'Scheduler', label: 'Scheduler', description: 'Administration API and scheduler', icon: 'pi pi-server' },
    { target: 'Daas', service: 'DaasSync', label: 'DaaS', description: 'DaaS synchronization service', icon: 'pi pi-database', source: 'Daas' },
    { target: 'AppDirect', service: 'AppDirectSync', label: 'AppDirect', description: 'AppDirect synchronization service', icon: 'pi pi-cloud', source: 'AppDirect' },
    { target: 'Webshop', service: 'WebshopSync', label: 'Webshop', description: 'Webshop synchronization service', icon: 'pi pi-shopping-cart', source: 'Webshop' },
    { target: 'ServiceNow', service: 'ServiceNowSync', label: 'ServiceNow', description: 'ServiceNow synchronization service', icon: 'pi pi-ticket', source: 'ServiceNow' },
  ];

  services: SyncServiceSummary[] = [];
  healthSummary?: SystemHealthSummary;
  servicesLoading = false;
  healthLoading = false;
  servicesError = '';
  healthError = '';
  readonly refreshingHealthTargets = new Set<SystemHealthTarget>();
  readonly healthRefreshErrors = new Map<SystemHealthTarget, string>();

  healthDialogVisible = false;
  healthDetailsLoading = false;
  healthDetailsError = '';
  healthReport?: ServiceHealthReport;
  selectedCard?: ServiceCard;
  referenceFeedback = '';
  private healthDetailsRequest?: Subscription;

  outboundIpDialogVisible = false;
  outboundIpLoading = false;
  outboundIpError = '';
  outboundIpReferenceId = '';
  outboundIpFeedback = '';
  outboundIpResult?: OutboundIpResult;
  private outboundIpRequest?: Subscription;

  get loading(): boolean {
    return this.servicesLoading || this.healthLoading || this.refreshingHealthTargets.size > 0;
  }

  get healthDialogTitle(): string {
    return `Health details — ${this.selectedCard?.label ?? 'Service'}`;
  }

  get outboundIpDialogTitle(): string {
    return `Outbound IP — ${this.selectedCard?.label ?? 'Service'}`;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loadServices();
    this.loadHealthSummary();
  }

  syncService(card: ServiceCard): SyncServiceSummary | undefined {
    return card.source
      ? this.services.find(service => service.source === card.source)
      : undefined;
  }

  healthState(card: ServiceCard): 'healthy' | 'unhealthy' | 'unavailable' | 'checking' {
    if ((this.healthLoading || this.isHealthRefreshing(card))
      && !this.serviceHealthSummary(card)) {
      return 'checking';
    }

    const status = this.serviceHealthSummary(card);
    if (!status) {
      return 'unavailable';
    }

    return status.healthy ? 'healthy' : 'unhealthy';
  }

  healthLabel(card: ServiceCard): string {
    const labels = {
      healthy: 'Healthy',
      unhealthy: 'Unhealthy',
      unavailable: 'Unavailable',
      checking: 'Checking…',
    };
    return labels[this.healthState(card)];
  }

  healthCheckedAt(card: ServiceCard): string | undefined {
    return this.serviceHealthSummary(card)?.checkedAtUtc;
  }

  isHealthRefreshing(card: ServiceCard): boolean {
    return this.refreshingHealthTargets.has(card.target);
  }

  healthRefreshError(card: ServiceCard): string {
    return this.healthRefreshErrors.get(card.target) ?? '';
  }

  refreshHealth(card: ServiceCard): void {
    if (this.healthLoading || this.isHealthRefreshing(card)) {
      return;
    }

    this.refreshingHealthTargets.add(card.target);
    this.healthRefreshErrors.delete(card.target);
    this.systemHealthApi.getServiceHealthSummary(card.target)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.refreshingHealthTargets.delete(card.target);
          this.changeDetector.markForCheck();
        }))
      .subscribe({
        next: (summary) => {
          this.updateServiceHealthSummary(summary);
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.removeServiceHealthSummary(card.service);
          this.healthRefreshErrors.set(card.target, `${card.label} health could not be checked.`);
          this.changeDetector.markForCheck();
        },
      });
  }

  openHealthDetails(card: ServiceCard): void {
    this.cancelHealthDetailsRequest();
    this.selectedCard = card;
    this.healthReport = undefined;
    this.healthDetailsError = '';
    this.referenceFeedback = '';
    this.healthDialogVisible = true;
    this.loadHealthDetails();
  }

  retryHealthDetails(): void {
    if (!this.selectedCard || this.healthDetailsLoading) {
      return;
    }

    this.healthReport = undefined;
    this.healthDetailsError = '';
    this.loadHealthDetails();
  }

  closeHealthDialog(): void {
    this.cancelHealthDetailsRequest();
    this.healthDialogVisible = false;
    this.healthDetailsLoading = false;
    this.healthReport = undefined;
    this.healthDetailsError = '';
    this.referenceFeedback = '';
    this.selectedCard = undefined;
    this.changeDetector.markForCheck();
  }

  openOutboundIpDialog(card: ServiceCard): void {
    this.cancelOutboundIpRequest();
    this.selectedCard = card;
    this.outboundIpResult = undefined;
    this.outboundIpError = '';
    this.outboundIpReferenceId = '';
    this.outboundIpFeedback = '';
    this.outboundIpDialogVisible = true;
    this.loadOutboundIp();
  }

  retryOutboundIp(): void {
    if (!this.selectedCard || this.outboundIpLoading) {
      return;
    }

    this.outboundIpResult = undefined;
    this.outboundIpError = '';
    this.outboundIpReferenceId = '';
    this.outboundIpFeedback = '';
    this.loadOutboundIp();
  }

  closeOutboundIpDialog(): void {
    this.cancelOutboundIpRequest();
    this.outboundIpDialogVisible = false;
    this.outboundIpLoading = false;
    this.outboundIpResult = undefined;
    this.outboundIpError = '';
    this.outboundIpReferenceId = '';
    this.outboundIpFeedback = '';
    this.selectedCard = undefined;
    this.changeDetector.markForCheck();
  }

  async copyReference(referenceId: string): Promise<void> {
    if (!referenceId) {
      return;
    }

    this.referenceFeedback = await this.copyText(
      referenceId,
      'Log reference copied.',
      `The reference could not be copied automatically: ${referenceId}`);
    this.changeDetector.markForCheck();
  }

  async copyOutboundIp(): Promise<void> {
    const address = this.outboundIpResult?.address;
    if (!address) {
      return;
    }

    this.outboundIpFeedback = await this.copyText(
      address,
      'IP address copied.',
      `The IP address could not be copied automatically: ${address}`);
    this.changeDetector.markForCheck();
  }

  async copyOutboundIpReference(): Promise<void> {
    if (!this.outboundIpReferenceId) {
      return;
    }

    this.outboundIpFeedback = await this.copyText(
      this.outboundIpReferenceId,
      'Log reference copied.',
      `The reference could not be copied automatically: ${this.outboundIpReferenceId}`);
    this.changeDetector.markForCheck();
  }

  dependencyLabel(name: string): string {
    const labels: Record<string, string> = {
      'scheduler-database': 'Scheduler database',
      cidaas: 'cidaas',
      daas: 'DaaS',
      appdirect: 'AppDirect',
      webshop: 'Webshop',
      'function-app': 'Function App',
    };
    return labels[name] ?? name;
  }

  private loadServices(): void {
    if (this.servicesLoading) {
      return;
    }

    this.servicesLoading = true;
    this.servicesError = '';
    this.syncServicesApi.getSyncServices()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.servicesLoading = false;
          this.changeDetector.markForCheck();
        }))
      .subscribe({
        next: (services) => {
          this.services = services;
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.servicesError = 'Sync service information could not be loaded.';
          this.changeDetector.markForCheck();
        },
      });
  }

  private loadHealthSummary(): void {
    if (this.healthLoading) {
      return;
    }

    this.healthLoading = true;
    this.healthError = '';
    this.systemHealthApi.getSystemHealthSummary()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.healthLoading = false;
          this.changeDetector.markForCheck();
        }))
      .subscribe({
        next: (summary) => {
          this.healthSummary = summary;
          this.healthRefreshErrors.clear();
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.healthSummary = undefined;
          this.healthError = 'Service health could not be checked.';
          this.changeDetector.markForCheck();
        },
      });
  }

  private serviceHealthSummary(card: ServiceCard): ServiceHealthSummary | undefined {
    return this.healthSummary?.services.find(service => service.service === card.service);
  }

  private updateServiceHealthSummary(updated: ServiceHealthSummary): void {
    const services = [
      ...(this.healthSummary?.services.filter(service => service.service !== updated.service) ?? []),
      updated,
    ];
    this.healthSummary = {
      healthy: services.length === this.cards.length && services.every(service => service.healthy),
      checkedAtUtc: updated.checkedAtUtc,
      services,
    };
  }

  private removeServiceHealthSummary(serviceName: string): void {
    if (!this.healthSummary) {
      return;
    }

    this.healthSummary = {
      ...this.healthSummary,
      healthy: false,
      services: this.healthSummary.services.filter(service => service.service !== serviceName),
    };
  }

  private loadHealthDetails(): void {
    const card = this.selectedCard;
    if (!card) {
      return;
    }

    this.healthDetailsLoading = true;
    this.healthDetailsRequest = this.systemHealthApi.getSystemHealth(card.target)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.healthDetailsLoading = false;
          this.changeDetector.markForCheck();
        }))
      .subscribe({
        next: (report: SystemHealthReport) => {
          this.healthReport = report.services[0];
          if (!this.healthReport) {
            this.healthDetailsError = 'No health details were returned.';
          }
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.healthDetailsError = 'Health details could not be loaded. Try again.';
          this.changeDetector.markForCheck();
        },
      });
  }

  private loadOutboundIp(): void {
    const card = this.selectedCard;
    if (!card) {
      return;
    }

    this.outboundIpLoading = true;
    this.outboundIpRequest = this.systemHealthApi.getOutboundIp(card.target)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.outboundIpLoading = false;
          this.changeDetector.markForCheck();
        }))
      .subscribe({
        next: (result) => {
          this.outboundIpResult = result;
          this.changeDetector.markForCheck();
        },
        error: (response: HttpErrorResponse) => {
          this.outboundIpError = response.error?.detail
            || 'The outbound IP address could not be retrieved. Try again.';
          this.outboundIpReferenceId = response.error?.referenceId || '';
          this.changeDetector.markForCheck();
        },
      });
  }

  private cancelHealthDetailsRequest(): void {
    this.healthDetailsRequest?.unsubscribe();
    this.healthDetailsRequest = undefined;
  }

  private cancelOutboundIpRequest(): void {
    this.outboundIpRequest?.unsubscribe();
    this.outboundIpRequest = undefined;
  }

  private async copyText(value: string, success: string, failure: string): Promise<string> {
    try {
      await navigator.clipboard.writeText(value);
      return success;
    } catch {
      return failure;
    }
  }
}
